import React, { Dispatch, SetStateAction, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ImagePlaceholder } from '@components/icons/icons/ImagePlaceholder';
import { themeColors } from '@styles/theme';
import { IconButton } from '@components/buttons/IconButton/IconButton';
import { PencilIcon } from '@components/icons/icons/PencilIcon';
import { DeleteIcon } from '@components/icons/icons/DeleteIcon';
import { ProjectEntity } from '@api/entities';
import { useTheme } from '@context/themeContext';
import { formatDataTime } from '@utils/utils';
import { CloseIcon } from '@components/icons/icons/CloseIcon';

export type ProjectsTableRowProps = {
    project: ProjectEntity;
    rowClickHandler: (projectId: string) => void;
    onDeleteFn: (id: string) => void;
    setProjectEditing: Dispatch<SetStateAction<ProjectEntity | null>>;
};

const ProjectsTableRow = ({
    project,
    rowClickHandler,
    onDeleteFn,
    setProjectEditing,
}: ProjectsTableRowProps) => {
    const { t } = useTranslation();
    const { theme } = useTheme();

    const handleRowClick = useCallback(
        (project: ProjectEntity) => {
            const { id } = project;
            rowClickHandler(id!);
        },
        [rowClickHandler]
    );

    const projectEditHandler = useCallback(
        (event, project: ProjectEntity) => {
            event.stopPropagation();
            setProjectEditing(project);
        },
        [setProjectEditing]
    );

    const projectDeleteHandler = useCallback(
        async (event, id: string) => {
            event.stopPropagation();
            onDeleteFn(id);
        },
        [onDeleteFn]
    );

    return (
        <>
            <tr
                className={`project-row`}
                key={project.id}
                onClick={() => handleRowClick(project)}
                data-testid={`project-row_${project.id}`}
            >
                <td>
                    <div className={`project-row__title`}>
                        <div>
                            <ImagePlaceholder
                                width={32}
                                height={32}
                                color={
                                    theme === 'light' ? themeColors.stroke : themeColors.navyBlue
                                }
                                backgroundColor={
                                    theme === 'light' ? themeColors.grey1 : themeColors.grey3
                                }
                            />
                        </div>
                        <p>{project.projectName}</p>
                    </div>
                </td>
                <td>
                    <div className={`project-row__time center`}>
                        <p>{formatDataTime(project.updateTs!)}</p>
                    </div>
                </td>
                <td>
                    <div className={`project-row__simulation-state center`}>
                        {project.projectExecutionStats?.completed}
                    </div>
                </td>
                <td>
                    <div className={`project-row__simulation-state center`}>
                        {project.projectExecutionStats?.running || (
                            <CloseIcon width={16} height={16} color={themeColors.white} />
                        )}
                    </div>
                </td>
                <td>
                    <div className={`project-row__controls center`}>
                        <IconButton
                            icon={PencilIcon}
                            onClick={event => projectEditHandler(event, project)}
                            data-testid={`edit_project_${project.id}`}
                        />
                        <IconButton
                            icon={DeleteIcon}
                            onClick={event => projectDeleteHandler(event, project.id!)}
                            data-testid={`delete_project_${project.id}`}
                        />
                    </div>
                </td>
                <td>
                    <div className={`project-row__empty right`}></div>
                </td>
            </tr>
        </>
    );
};

export default ProjectsTableRow;
